import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { color } from '@schmancy/directives'
import { SchmancyTheme } from '@schmancy/theme/theme.interface'
import { identityTint, type SchmancyTone, type ToneVariant } from '@schmancy/theme/tone'
import '../tone-dot'

/**
 * The status mark is `<schmancy-tone-dot>` — the library's point mark —
 * rather than a dot of the avatar's own, so "a filled circle signalling a
 * tri-state" has exactly one implementation. `offline` is the family's
 * `hollow` variant: the page ground and no tone, its boundary drawn by the
 * host below, because only the host knows what sits behind it.
 */
const STATUS_TONE: Record<AvatarStatus, { color: SchmancyTone; variant: ToneVariant }> = {
	online: { color: 'success', variant: 'solid' },
	offline: { color: 'neutral', variant: 'hollow' },
	busy: { color: 'error', variant: 'solid' },
	away: { color: 'tertiary', variant: 'solid' },
	none: { color: 'neutral', variant: 'solid' },
}

const STATUS_LABEL: Record<AvatarStatus, string> = {
	online: 'Online',
	offline: 'Offline',
	busy: 'Busy',
	away: 'Away',
	none: '',
}

export type AvatarColor = 'primary' | 'secondary' | 'tertiary' | 'success' | 'error' | 'neutral' | 'transparent'
export type AvatarShape = 'circle' | 'square'
export type AvatarStatus = 'online' | 'offline' | 'busy' | 'away' | 'none'

/**
 * Sizing is the host's box: `:host` sets a 2.5rem default that any Tailwind
 * `w-*`/`h-*` on the host overrides (author styles outrank `:host` at equal
 * specificity). Content fills the host and initials scale via container-query
 * units — legible at any size, no size enum.
 *
 * @slot glyph - A mark of the consumer's own, such as a brand SVG, in place of
 *   the initials and the icon. It inherits the avatar's ink as `currentColor` and
 *   sits in a 60% box whose font-size is that box, so an SVG drawn at
 *   `width="1em" height="1em" fill="currentColor"` fills it and follows the tint.
 *   A loaded `src` still wins over it.
 */
@customElement('schmancy-avatar')
export class SchmancyAvatar extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
				width: 2.5rem;
				height: 2.5rem;
				container-type: size;
				border-radius: var(--schmancy-radius-full, calc(infinity * 1px)); /* mirrors rounded-full on the inner disk */

				/* Depth lives on the host, as neutral light at the boundary — never the
				   identity hue (DEPTH_IS_LIGHT_AT_THE_BOUNDARY: brand color is the accent
				   layer, not the depth layer — and here color is per-instance identity
				   DATA, not decoration, so tinting depth from it would conflate "who this
				   is" with "current interaction state"). Layer 1 is a soft ambient drop
				   shadow (material grounding); layers 2-4 are a fixed-px hairline — a
				   light top edge, a darker bottom edge, and a 1px all-around ring — that
				   gives every disk a legible boundary even when two adjacent avatars in
				   a name list land in the same identity-color bucket (only 6 buckets exist;
				   collisions are expected past ~7 people on screen). white/black here are
				   literal structural light/dark, not brand color — physically modeling a
				   lit, faintly bevelled disk, the same vocabulary Material elevation
				   overlays use. Kept fixed-px (not cqmin-scaled): a hairline that shrunk
				   with the box would vanish at 24px; one that grew would look like a
				   heavy border at 96px. */
				box-shadow:
					0 1px 3px color-mix(in srgb, var(--schmancy-sys-color-shadow) 20%, transparent),
					inset 0 1px 0 0 color-mix(in srgb, white 45%, transparent),
					inset 0 -1px 1.5px 0 color-mix(in srgb, black 18%, transparent),
					inset 0 0 0 1px color-mix(in srgb, white 12%, transparent);

				transition:
					transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1),
					box-shadow 220ms ease,
					filter 200ms ease;
			}

			/* shape reflects (see props) so the host's own box-shadow/rounding follows
			   the same silhouette as the inner div's. */
			:host([shape='square']) {
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* mirrors --radius-lg (rounded-lg) */
			}

			/* Hover/press physics are opt-in via 'interactive' — a decorative avatar
			   in a name list with no click behavior stays perfectly still, so hover
			   never promises an affordance it doesn't have, and a name list of
			   hundreds pays zero idle cost (pure CSS, no per-instance listener). Numbers borrowed
			   from list-item.ts's own physics (spring cubic-bezier, color-mix-in-srgb
			   vocabulary) so an interactive avatar feels like the same material as a
			   clickable row around it. */
			:host([interactive]) {
				cursor: pointer;
			}

			:host([interactive]:hover) {
				transform: scale(1.045);
				filter: brightness(1.04);
				/* Idle hairline layers persist (not replaced) so the edge stays legible
				   mid-hover; only the ambient shadow widens/lifts. */
				box-shadow:
					0 4px 10px -2px color-mix(in srgb, var(--schmancy-sys-color-shadow) 30%, transparent),
					inset 0 1px 0 0 color-mix(in srgb, white 50%, transparent),
					inset 0 -1px 1.5px 0 color-mix(in srgb, black 20%, transparent),
					inset 0 0 0 1px color-mix(in srgb, white 14%, transparent);
			}

			:host([interactive]:active) {
				transform: scale(0.96);
				transition-duration: 100ms;
			}

			/* A ghost mark is not a lit disk: the bevel and drop shadow above model a
			   filled surface, so on a mark with no fill they paint a phantom disk
			   around it. Declared after the interactive rules — equal specificity,
			   later source order — so an interactive ghost stays flat on hover too,
			   while every other color keeps its depth untouched. */
			:host([color='transparent']),
			:host([color='transparent']:hover) {
				box-shadow: none;
			}

			@media (prefers-reduced-motion: reduce) {
				:host {
					transition: box-shadow 150ms ease;
				}
				:host([interactive]:hover),
				:host([interactive]:active) {
					transform: none;
					filter: none;
				}
			}
		`,
	]

	@property({ type: String }) initials: string = ''
	@property({ type: String }) src: string = ''
	@property({ type: String }) icon: string = ''
	/**
	 * `transparent` is the ghost mark: no fill, no bevel, the glyph or initials
	 * inked in the ambient on-surface tone — the only value that reads as an
	 * outline or bare mark rather than a filled disk. Reflected so the host's
	 * own depth layers can stand down for it (see `:host([color='transparent'])`
	 * above); every other value renders exactly as before.
	 */
	@property({ type: String, reflect: true }) color?: AvatarColor
	@property({ type: String, reflect: true }) shape: AvatarShape = 'circle'
	@property({ type: Boolean }) bordered: boolean = false
	@property({ type: String }) status: AvatarStatus = 'none'
	@property({ type: String }) name: string = ''
	@property({ type: Boolean, reflect: true }) interactive: boolean = false

	/** A node is assigned to the `glyph` slot; it then takes the place of the
	 *  initials and the icon. */
	@state() private hasGlyph = false
	@state() private loaded = false
	@state() private imgError = false

	private static readonly COLOR_MAP: Record<AvatarColor, { bgColor: string; color: string }> = {
		primary: { bgColor: SchmancyTheme.sys.color.primary.container, color: SchmancyTheme.sys.color.primary.onContainer },
		secondary: {
			bgColor: SchmancyTheme.sys.color.secondary.container,
			color: SchmancyTheme.sys.color.secondary.onContainer,
		},
		tertiary: {
			bgColor: SchmancyTheme.sys.color.tertiary.container,
			color: SchmancyTheme.sys.color.tertiary.onContainer,
		},
		success: { bgColor: SchmancyTheme.sys.color.success.container, color: SchmancyTheme.sys.color.success.onContainer },
		error: { bgColor: SchmancyTheme.sys.color.error.container, color: SchmancyTheme.sys.color.error.onContainer },
		neutral: { bgColor: SchmancyTheme.sys.color.surface.container, color: SchmancyTheme.sys.color.surface.on },
		// The ghost: whatever is behind the avatar shows through, and the mark
		// inks in the ambient on-surface tone. `bordered` still draws a ring, so
		// an outline avatar is `color="transparent" bordered`.
		transparent: { bgColor: 'transparent', color: SchmancyTheme.sys.color.surface.on },
	}

	/** djb2-style string hash — deterministic, no dependency. */
	private static hashSeed(seed: string): number {
		let hash = 0
		for (let i = 0; i < seed.length; i++) {
			hash = (hash << 5) - hash + seed.charCodeAt(i)
			hash |= 0
		}
		return Math.abs(hash)
	}

	protected willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		// Re-mounting with a new src replays the fade-in and clears any prior broken-image state.
		if (changed.has('src')) {
			this.loaded = false
			this.imgError = false
		}
	}

	// `interactive` promises a clickable row (cursor: pointer, hover/press
	// physics — see the doc's `@click=${openProfile}` example) but a pointer
	// affordance with no keyboard path is only half a control: a div carries
	// no native Enter/Space activation the way a <button> does. Mirrors
	// list-item.ts's identical onKeydown in this same package — fire once
	// (no repeat-driven double-activation), dispatch a real click so it
	// reaches whatever @click the consumer bound on the host.
	private onKeydown = (e: KeyboardEvent): void => {
		if (!this.interactive) return
		if (e.key !== 'Enter' && e.key !== ' ') return
		if (e.repeat) return
		e.preventDefault()
		this.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true, cancelable: true }))
	}

	render() {
		const seed = (this.name || this.initials || this.icon || 'avatar').trim().toLowerCase()
		// Explicit `color` (including color="primary") always wins — undefined
		// (the default) is the only trigger for identity derivation.
		const identity = identityTint(SchmancyAvatar.hashSeed(seed))
		const tint = this.color
			? SchmancyAvatar.COLOR_MAP[this.color]
			: { bgColor: identity.container, color: identity.onContainer }
		const baseName = this.ariaLabel || this.name || this.initials || 'Avatar'
		const accessibleName = this.status !== 'none' ? `${baseName}, ${STATUS_LABEL[this.status]}` : baseName

		return html`
			<div
				role=${this.interactive ? 'button' : 'img'}
				aria-label="${accessibleName}"
				tabindex=${ifDefined(this.interactive ? 0 : undefined)}
				@keydown=${this.onKeydown}
				class="${this.classMap({
					'w-full h-full relative flex items-center justify-center': true,
					[this.shape === 'circle' ? 'rounded-full' : 'rounded-lg']: true,
					'border-2 border-surface-container': this.bordered,
					'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 outline-secondary-default outline-hidden':
						this.interactive,
				})}"
				${color(tint)}
			>
				<!-- The circular mask wraps the media ALONE. Put overflow-hidden on the
				     element that also hosts the status dot and the mask eats the dot: it
				     sits at the bottom-right corner, which for a circle is outside the
				     disk, so it renders as a crescent. Layout is unaffected by the clip,
				     which is why the dot's own getBoundingClientRect() reads full-size
				     either way — only hit-testing and paint tell the two apart. -->
				<div
					class="${this.classMap({
						'w-full h-full flex items-center justify-center overflow-hidden': true,
						[this.shape === 'circle' ? 'rounded-full' : 'rounded-lg']: true,
					})}"
				>
					${when(
						this.src && !this.imgError,
						() =>
							html`<img
								class="${this.classMap({
									'w-full h-full object-cover transition-opacity duration-300 ease-out': true,
									'opacity-0': !this.loaded,
									'opacity-100': this.loaded,
								})}"
								src="${this.src}"
								alt=""
								aria-hidden="true"
								@load=${() => {
									this.loaded = true
								}}
								@error=${() => {
									this.imgError = true
								}}
							/>`,
						() =>
							when(
								!!this.initials && !this.hasGlyph,
								() =>
									html`<span aria-hidden="true" class="font-medium leading-none text-[40cqmin]"
										>${this.initials.substring(0, 2).toUpperCase()}</span
									>`,
								() =>
									when(
										!this.hasGlyph,
										() =>
											html`<schmancy-icon aria-hidden="true" class="text-[60cqmin]"
												>${this.icon || 'person'}</schmancy-icon
											>`,
									),
							),
					)}
					<!-- Always rendered, so an assignment is heard; hidden while a loaded
					     image owns the disk. -->
					<slot
						name="glyph"
						aria-hidden="true"
						class="${this.classMap({
							'flex items-center justify-center text-[60cqmin] leading-none': true,
							hidden: !!this.src && !this.imgError,
						})}"
						@slotchange=${(e: Event) => {
							this.hasGlyph = (e.target as HTMLSlotElement).assignedNodes({ flatten: true }).length > 0
						}}
					></slot>
				</div>
				${when(
					this.status !== 'none',
					() =>
						html`<schmancy-tone-dot
							aria-hidden="true"
							color=${STATUS_TONE[this.status].color}
							variant=${STATUS_TONE[this.status].variant}
							class="${this.classMap({
								// 26cqmin scales the dot with the host, but a 24px avatar in a name list
								// (a real, tested size — see size-extremes) puts it under 7px,
								// where a 2px ring and a filled/hollow distinction both turn to
								// mud. max() keeps a legible floor at small hosts while still
								// scaling normally once cqmin overtakes it.
								'absolute bottom-0 right-0 ring-2 w-[max(10px,26cqmin)] h-[max(10px,26cqmin)]': true,
								// The dot paints its own fill and nothing else; the ring that
								// separates it from the disk behind it is this host's, because
								// this host is the only element that knows what that disk is.
								'ring-surface-default': this.status !== 'offline',
								'ring-surface-on-variant': this.status === 'offline',
							})}"
						></schmancy-tone-dot>`,
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-avatar': SchmancyAvatar
	}
}
